import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  FaSpinner,
  FaCalendarAlt,
  FaExclamationTriangle,
  FaChartBar,
  FaChevronDown,
  FaChevronUp,
  FaCogs,
  FaChartLine,
  FaCoins,
  FaReceipt,
  FaWallet,
} from "react-icons/fa";
import apiRequest from "../services/api";

export default function CashFlowPage() {
  const today = new Date();
  const formatDate = (date) => date.toISOString().split("T")[0];
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 7);

  const currentYear = today.getFullYear();
  const [startDate, setStartDate] = useState(`${currentYear}-01-01`);

  const [endDate, setEndDate] = useState(
    `${currentYear}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
      today.getDate()
    ).padStart(2, "0")}`
  );

  const [data, setData] = useState({
    openingCashBalance: 0,
    operatingCashFlow: 0,
    operatingDetails: [],
    investingCashFlow: 0,
    investingDetails: [],
    financingCashFlow: 0,
    financingDetails: [],
    netCashMovement: 0,
    closingCashBalance: 0,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [expandedSection, setExpandedSection] = useState({
    OPERATING: true,
    INVESTING: false,
    FINANCING: false,
  });

  const formatCurrency = (value) => {
    const num = Number(value || 0);
    return `ETB ${num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", `${startDate}T00:00:00`);
      if (endDate) params.append("endDate", `${endDate}T23:59:59`);

      const res = await apiRequest(
        `reports/cashflow-summary?${params.toString()}`
      );
      const payload = res?.data || res;

      setData({
        openingCashBalance: payload?.openingCashBalance || 0,
        operatingCashFlow: payload?.operatingCashFlow || 0,
        operatingDetails: payload?.operatingDetails || [],
        investingCashFlow: payload?.investingCashFlow || 0,
        investingDetails: payload?.investingDetails || [],
        financingCashFlow: payload?.financingCashFlow || 0,
        financingDetails: payload?.financingDetails || [],
        netCashMovement: payload?.netCashMovement || 0,
        closingCashBalance: payload?.closingCashBalance || 0,
      });
    } catch (err) {
      setError(
        err?.message || "Failed to load cash flow statement. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const toggleSection = (key) => {
    setExpandedSection((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const amountColor = (value) =>
    Number(value || 0) >= 0
      ? "text-emerald-600 dark:text-emerald-400"
      : "text-rose-600 dark:text-rose-400";

  const sections = useMemo(
    () => [
      {
        key: "OPERATING",
        title: "Operating Activities",
        icon: <FaCogs className="text-emerald-600 dark:text-emerald-400" />,
        total: data.operatingCashFlow || 0,
        details: data.operatingDetails || [],
        badge:
          "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20",
      },
      {
        key: "INVESTING",
        title: "Investing Activities",
        icon: <FaChartLine className="text-blue-600 dark:text-blue-400" />,
        total: data.investingCashFlow || 0,
        details: data.investingDetails || [],
        badge:
          "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20",
      },
      {
        key: "FINANCING",
        title: "Financing Activities",
        icon: <FaCoins className="text-purple-600 dark:text-purple-400" />,
        total: data.financingCashFlow || 0,
        details: data.financingDetails || [],
        badge:
          "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-500/10 dark:text-purple-400 dark:border-purple-500/20",
      },
    ],
    [data]
  );

  const inputCls =
    "w-full rounded-xl pl-10 pr-4 py-2 text-sm transition focus:outline-none focus:border-emerald-500/50 bg-white border border-slate-200 text-slate-800 dark:bg-slate-900/50 dark:border-white/[0.08] dark:text-slate-200";

  return (
    <div className="min-h-full relative overflow-hidden font-sans p-3 md:p-6 bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100">
      {/* Background glows */}
      <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full pointer-events-none bg-blue-500/5 blur-[120px] dark:bg-blue-500/10" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] rounded-full pointer-events-none bg-emerald-500/5 blur-[150px] dark:bg-emerald-500/10" />

      <div className="max-w-7xl mx-auto relative z-10 space-y-6">
        {/* HEADER */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between p-6 rounded-2xl gap-4 border shadow-sm backdrop-blur-xl bg-white border-slate-200 dark:bg-white/[0.03] dark:border-white/[0.08] dark:shadow-2xl">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 dark:bg-gradient-to-r dark:from-white dark:via-slate-200 dark:to-slate-400 dark:bg-clip-text dark:text-transparent flex items-center gap-3">
              <FaChartBar className="text-emerald-600 dark:text-emerald-400" />
              Cash Flow Statement
            </h1>
            <p className="text-xs mt-1 text-slate-500 dark:text-slate-400">
              Cash movement analysis • Operating, Investing & Financing
            </p>
          </div>
        </div>

        {/* DATE FILTERS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 rounded-xl border backdrop-blur-md bg-white border-slate-200 dark:bg-white/[0.02] dark:border-white/[0.05]">
          <div>
            <label className="text-xs text-slate-500 dark:text-slate-400">
              Start Date
            </label>
            <div className="relative flex items-center mt-1">
              <FaCalendarAlt className="absolute left-3 text-slate-400 dark:text-slate-500" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-500 dark:text-slate-400">
              End Date
            </label>
            <div className="relative flex items-center mt-1">
              <FaCalendarAlt className="absolute left-3 text-slate-400 dark:text-slate-500" />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
        </div>

        {/* LOADING */}
        {loading && (
          <div className="text-center py-12 rounded-2xl border backdrop-blur-md text-slate-500 bg-white border-slate-200 dark:text-slate-400 dark:bg-white/[0.01] dark:border-white/[0.05]">
            <FaSpinner className="animate-spin inline mr-3 text-blue-500 text-lg" />
            Loading cash flow statement...
          </div>
        )}

        {/* ERROR */}
        {error && (
          <div className="flex items-center gap-3 justify-center text-center py-8 rounded-2xl border text-rose-600 bg-rose-50 border-rose-200 dark:text-rose-400 dark:bg-rose-950/20 dark:border-rose-500/20">
            <FaExclamationTriangle />
            <span>{error}</span>
          </div>
        )}

        {/* CONTENT */}
        {!loading && !error && (
          <div className="w-full space-y-6">
            {/* CASH SUMMARY */}
            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-4 p-5 rounded-2xl border shadow-sm backdrop-blur-xl
              bg-white border-slate-200
              dark:bg-white/[0.02] dark:border-white/[0.08] dark:shadow-2xl"
            >
              <SummaryCard
                title="Opening Cash"
                value={data.openingCashBalance}
                formatMoney={formatCurrency}
              />

              <div
                className="p-4 rounded-xl border
                border-emerald-200 bg-emerald-50
                dark:border-emerald-500/20 dark:bg-emerald-500/[0.03]"
              >
                <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                  Closing Cash
                </span>
                <h3
                  className={`text-2xl font-mono font-black mt-1 ${amountColor(
                    data.closingCashBalance
                  )}`}
                >
                  {formatCurrency(data.closingCashBalance)}
                </h3>
              </div>
            </div>

            {/* SECTIONS */}
            <div className="space-y-4">
              {sections.map((section) => {
                const expanded = expandedSection[section.key];
                const details = section.details || [];

                return (
                  <div
                    key={section.key}
                    className="overflow-hidden rounded-2xl border shadow-sm
                      bg-white border-slate-200
                      dark:bg-white/[0.02] dark:border-white/[0.08] dark:shadow-xl"
                  >
                    {/* HEADER */}
                    <div
                      onClick={() => toggleSection(section.key)}
                      className="p-4 border-b flex items-center justify-between cursor-pointer transition
                        bg-slate-50 border-slate-200 hover:bg-slate-100
                        dark:bg-slate-950/60 dark:border-white/[0.08] dark:hover:bg-white/[0.03]"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="p-2.5 rounded-xl border
                          bg-white border-slate-200
                          dark:bg-white/[0.05] dark:border-white/[0.08]"
                        >
                          {section.icon}
                        </div>

                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-bold text-slate-800 dark:text-white">
                              {section.title}
                            </h4>
                            <span
                              className={`text-[10px] px-2 py-0.5 rounded-full border font-mono ${section.badge}`}
                            >
                              {section.key}
                            </span>
                          </div>
                          <span className="text-[10px] text-slate-500 dark:text-slate-400">
                            {details.length} transaction entries
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <span
                          className={`font-mono font-bold text-sm ${amountColor(
                            section.total
                          )}`}
                        >
                          {formatCurrency(section.total)}
                        </span>
                        {expanded ? (
                          <FaChevronUp className="text-slate-400" />
                        ) : (
                          <FaChevronDown className="text-slate-400" />
                        )}
                      </div>
                    </div>

                    {/* TABLE */}
                    {expanded && (
                      <div className="p-4">
                        {details.length === 0 ? (
                          <div className="text-center py-6 text-xs italic text-slate-500">
                            No transactions recorded.
                          </div>
                        ) : (
                          <div
                            className="overflow-x-auto rounded-xl border
                            border-slate-200 bg-slate-50/50
                            dark:border-white/[0.08] dark:bg-slate-950/40"
                          >
                            <table className="w-full text-left">
                              <thead>
                                <tr
                                  className="text-[10px] uppercase border-b
                                  bg-slate-100/80 text-slate-500 border-slate-200
                                  dark:bg-white/[0.02] dark:text-slate-400 dark:border-white/[0.08]"
                                >
                                  <th className="p-3">Date</th>
                                  <th className="p-3">Cash Account</th>
                                  <th className="p-3">Reference</th>
                                  <th className="p-3">Memo</th>
                                  <th className="p-3 text-right">Amount</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y text-xs divide-slate-100 dark:divide-white/[0.04]">
                                {details.map((item, index) => (
                                  <tr
                                    key={index}
                                    className="transition
                                      text-slate-600 hover:bg-slate-50
                                      dark:text-slate-300 dark:hover:bg-white/[0.02]"
                                  >
                                    <td className="p-3 font-mono text-slate-500 dark:text-slate-400 whitespace-nowrap">
                                      {item.date
                                        ? new Date(item.date).toLocaleString()
                                        : "-"}
                                    </td>
                                    <td className="p-3 text-slate-800 dark:text-white">
                                      {item.cashAccount || "-"}
                                    </td>
                                    <td className="p-3">
                                      <div className="flex items-center gap-2 font-semibold text-slate-700 dark:text-slate-200">
                                        {item.reference && (
                                          <FaReceipt className="text-slate-400 dark:text-slate-500" />
                                        )}
                                        {item.reference || "-"}
                                      </div>
                                    </td>
                                    <td className="p-3 text-xs text-slate-500 dark:text-slate-400">
                                      {item.memo || "-"}
                                    </td>
                                    <td
                                      className={`p-3 text-right font-mono font-bold ${amountColor(
                                        item.amount
                                      )}`}
                                    >
                                      {formatCurrency(item.amount)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ title, value, formatMoney }) {
  return (
    <div
      className="p-4 rounded-xl border
      bg-slate-50 border-slate-200
      dark:bg-slate-900/60 dark:border-white/[0.05]"
    >
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
        {title}
      </span>
      <h3 className="text-xl font-mono font-bold mt-1 text-slate-800 dark:text-slate-200">
        {formatMoney(value)}
      </h3>
    </div>
  );
}
